import cn from 'classnames';
import React, { FC, useEffect, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/common/Button';
import PopUpContainer from 'src/components/common/modal/PopUp';
import { Typography } from 'src/components/common/Typography';
import { useGDLibContext } from 'src/contexts/GDLibContext';
import { setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useGameBoxReady } from 'src/hooks/use-gamebox-ready';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { useGameAction } from 'src/hooks/useGameAction';
import { MediaManager } from 'src/manager/MediaManager';
import { setCloseUserSettings, setContinuePlayGame } from 'src/store/app/actions';
import { RootState } from 'src/types/store-types';
import './styles.scss';

const GAMEBOX_WIDGET = 'gamebox-widget';

type Props = {
  settings: {
    needLogin: boolean;
    priority: number;
    styles: Styles;
    classes: string;
  };
};
const GameBoxToggle: FC<Props> = ({ settings }) => {
  const { t }: Translation = useTranslation();

  const { styles, classes } = settings;
  const sessionRecorder = useSessionRecorder();

  const _setCloseUserSettings = useConnectedAction(setCloseUserSettings);
  const _setContinuePlayGame = useConnectedAction(setContinuePlayGame);

  const { kycStatus } = useSelector((state: RootState) => state.user);
  const { isGameBoxOpen, showUserSetting } = useSelector((state: RootState) => state.app);
  const { actualMissions } = useSelector((state: RootState) => state.integrations);

  // Get GDLib context
  const { initializeGDLib } = useGDLibContext();

  const { shouldShowKyc, shouldShowLimits } = useGameAction();

  // Use GameBox readiness hook
  const gameBoxReady = useGameBoxReady({
    onSuccess: () => {
      sessionRecorder?.trackEvent('Header "gamebox" button click');
      isMobileOnly && setBodyOverflow('unset');
      _setContinuePlayGame(false);
      window.__gdlib?.openGameBoxInLobbyWidget({
        parentId: GAMEBOX_WIDGET,
        customClassName: `frame`,
        kycStatus: shouldShowKyc ? kycStatus : null,
        limitsStatus: shouldShowLimits,
      });
    },
    getBaseDescription: () => t('gameSlowText'),
    initializeGDLib,
  });

  const missionsCount = useMemo(() => {
    if (actualMissions === 0) return;

    return <span className="actual_missions">{actualMissions}</span>;
  }, [actualMissions]);

  const onToggle = (): void => {
    // Check GameBox readiness using hook
    if (!gameBoxReady.checkReadiness()) {
      console.log('[GameBoxToggle] GameBox not ready, showing popup...');
      setBodyOverflow('unset');
      gameBoxReady.showPopup();
      return;
    }

    console.log('[GameBoxToggle] Success! The gameBox toggle has been opened.');
    sessionRecorder?.trackEvent('Header "gamebox" button click');
    isMobileOnly && setBodyOverflow('unset');
    _setContinuePlayGame(false);
    window.__gdlib?.openGameBoxInLobbyWidget({
      parentId: GAMEBOX_WIDGET,
      customClassName: `frame`,
      kycStatus: shouldShowKyc ? kycStatus : null,
      limitsStatus: shouldShowLimits,
    });
  };

  useEffect(() => {
    if (isGameBoxOpen) {
      onToggle();
      showUserSetting && _setCloseUserSettings();
    }

    isMobileOnly && setBodyOverflow(isGameBoxOpen ? 'unset' : 'set');
  }, [isGameBoxOpen]);

  const handleClosePopup = (): void => {
    isMobileOnly && setBodyOverflow('set');
    gameBoxReady.handleClose();
  };

  return (
    <>
      {gameBoxReady.isPopUpOpen && (
        <PopUpContainer
          isOpen={gameBoxReady.isPopUpOpen}
          hasOverlay
          handleClose={handleClosePopup}
          handleOK={gameBoxReady.handleRetry}
          description={gameBoxReady.description}
        />
      )}
      <div className="gameBox_toggle_wrapper" onClick={() => onToggle()}>
        <Button
          ariaLabelledby="gameBoxWidget"
          fontWeight="bold"
          variant="contained"
          color="transparent"
          className={cn('toggle_wrapper', {
            [classes]: !!classes,
            opened: isGameBoxOpen,
          })}
          style={styles}
        >
          <img src={MediaManager.getSrcFromMediaAssets('gbIcon.png', '/')} alt="gameBox" />
        </Button>
        <Typography className="label" variant={'h6'}>
          {missionsCount}
        </Typography>
      </div>
    </>
  );
};
export default GameBoxToggle;
